import { useMemo } from 'react';
import { TooltipRow, useTooltip, TooltipLayer } from './Tooltip';
import { useMeasure } from './useMeasure';

export interface StackedSeries {
  key: string;
  label: string;
  color: string;
}

export interface StackedColumnsProps {
  categories: string[];
  categoryLabels?: string[];
  series: StackedSeries[];
  /** values[categoryIndex][seriesKey] */
  values: Record<string, number>[];
  height?: number;
  onSelectCategory?: (category: string) => void;
  selectedCategory?: string;
  /** Enhet som visas efter värden, t.ex. " %" när staplarna är normaliserade. */
  valueSuffix?: string;
}

const MARGIN = { top: 16, right: 12, bottom: 34, left: 44 };
const SEGMENT_GAP = 2;
const CORNER = 4;
const MIN_COLUMN_WIDTH = 34;

/**
 * Staplade kolumner med 2 px yta mellan segmenten och rundad topp på hela
 * stapeln. Segmenten ritas nerifrån och upp så att baslinjen alltid är platt.
 */
export function StackedColumns({
  categories,
  categoryLabels,
  series,
  values,
  height = 280,
  onSelectCategory,
  selectedCategory,
  valueSuffix = '',
}: StackedColumnsProps) {
  const { tooltip, show, hide } = useTooltip();
  const [containerRef, containerWidth] = useMeasure<HTMLDivElement>();

  const totals = useMemo(
    () => values.map((row) => series.reduce((sum, s) => sum + (row[s.key] ?? 0), 0)),
    [values, series],
  );
  const max = Math.max(1, ...totals);
  const ticks = useMemo(() => niceTicks(max, 4), [max]);
  const axisMax = ticks[ticks.length - 1];

  // Fyll panelen när månaderna är få, och låt diagrammet svämma över i
  // vågrät scroll först när de blir så många att kolumnerna skulle klämmas ihop.
  const width = Math.max(containerWidth, MARGIN.left + MARGIN.right + categories.length * MIN_COLUMN_WIDTH);
  const plotWidth = width - MARGIN.left - MARGIN.right;
  const plotHeight = height - MARGIN.top - MARGIN.bottom;
  const step = plotWidth / Math.max(1, categories.length);
  const barWidth = Math.min(38, step * 0.62);

  const yOf = (value: number) => MARGIN.top + plotHeight - (value / axisMax) * plotHeight;

  return (
    <>
      <div className="chart-scroll" ref={containerRef}>
        <svg
          className="chart"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMinYMin meet"
          role="img"
          aria-label={`Uppdrag per månad fördelat på ${series.length} ${series.length === 1 ? 'område' : 'områden'}`}
        >
          {ticks.map((tick) => (
            <g key={tick}>
              <line className="chart__grid" x1={MARGIN.left} x2={width - MARGIN.right} y1={yOf(tick)} y2={yOf(tick)} />
              <text className="chart__tick" x={MARGIN.left - 8} y={yOf(tick) + 4} textAnchor="end">
                {tick}
                {valueSuffix}
              </text>
            </g>
          ))}

          {categories.map((category, index) => {
            const row = values[index] ?? {};
            const x = MARGIN.left + step * index + (step - barWidth) / 2;
            const isSelected = selectedCategory === category;
            let cursor = 0;

            return (
              <g key={category}>
                {series.map((s, seriesIndex) => {
                  const value = row[s.key] ?? 0;
                  if (value <= 0) return null;
                  const bottom = yOf(cursor);
                  cursor += value;
                  const top = yOf(cursor);
                  const rawHeight = bottom - top;
                  const isTop = series.slice(seriesIndex + 1).every((later) => (row[later.key] ?? 0) <= 0);
                  const segmentHeight = Math.max(1, rawHeight - SEGMENT_GAP);

                  return (
                    <rect
                      key={s.key}
                      x={x}
                      y={top}
                      width={barWidth}
                      height={segmentHeight}
                      fill={s.color}
                      rx={isTop ? CORNER : 0}
                      opacity={selectedCategory && !isSelected ? 0.45 : 1}
                      onMouseMove={(event) =>
                        show(
                          event,
                          <>
                            <div className="tooltip__title">{categoryLabels?.[index] ?? category}</div>
                            {series
                              .filter((item) => (row[item.key] ?? 0) > 0)
                              .map((item) => (
                                <TooltipRow
                                  key={item.key}
                                  color={item.color}
                                  label={item.label}
                                  value={`${row[item.key]}${valueSuffix}`}
                                />
                              ))}
                            {valueSuffix ? null : <TooltipRow label="Totalt" value={totals[index]} />}
                          </>,
                        )
                      }
                      onMouseLeave={hide}
                    />
                  );
                })}

                <rect
                  className="chart__hit"
                  x={MARGIN.left + step * index}
                  y={MARGIN.top}
                  width={step}
                  height={plotHeight}
                  onClick={() => onSelectCategory?.(category)}
                >
                  <title>{`${categoryLabels?.[index] ?? category}: ${totals[index]} uppdrag`}</title>
                </rect>

                <text
                  className="chart__tick"
                  x={MARGIN.left + step * index + step / 2}
                  y={height - MARGIN.bottom + 16}
                  textAnchor="middle"
                  fontWeight={isSelected ? 700 : 400}
                  fill={isSelected ? 'var(--text-primary)' : undefined}
                >
                  {categoryLabels?.[index] ?? category}
                </text>
              </g>
            );
          })}

          <line
            className="chart__axis"
            x1={MARGIN.left}
            x2={width - MARGIN.right}
            y1={MARGIN.top + plotHeight}
            y2={MARGIN.top + plotHeight}
          />
        </svg>
      </div>
      <TooltipLayer tooltip={tooltip} />
    </>
  );
}

export function niceTicks(max: number, count: number): number[] {
  const rough = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(Math.max(rough, 1)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((candidate) => candidate >= rough) ?? magnitude * 10;
  const ticks: number[] = [];
  for (let value = 0; value <= max + step; value += step) ticks.push(Math.round(value));
  return ticks;
}
